import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import { NoBackend } from "@/components/common/NoBackend";
import { CatalogueCard } from "@/components/grow/CatalogueCard";
import { PlayPageHeader } from "@/components/grow/PlayPageHeader";
import { MyGameSessionsDocument } from "@/graphql/generated/operations";
import { gameGroupProgress, gameTileStatus } from "@/lib/games/game-status";
import { gameGroupByKey } from "@/lib/games/registry";

// One group of games, e.g. the eight Top 10 lists: the group page of
// questionnaire groups, with the games as tiles that carry their own status.
export function GameGroupPage() {
  const intl = useIntl();
  const { groupKey = "" } = useParams();
  const group = gameGroupByKey(groupKey);
  // cache-and-network so a game finished a moment ago shows as completed.
  const { data, loading } = useQuery(MyGameSessionsDocument, {
    skip: !group,
    fetchPolicy: "cache-and-network",
  });
  const sessions = data?.myGameSessions ?? [];

  if (!group) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12">
        <PlayPageHeader />
        <NoBackend
          label={intl.formatMessage({
            id: "grow.games.unknown",
            defaultMessage: "This game is not available here.",
          })}
        />
      </section>
    );
  }

  const { done, total } = gameGroupProgress(
    sessions,
    group.games.map((game) => game.key),
  );
  return (
    <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="MyGameSessions">
      <PlayPageHeader
        title={intl.formatMessage({ id: group.titleId })}
        description={intl.formatMessage({ id: group.descriptionId })}
      />
      {loading && !data ? (
        <p className="text-smycles-body text-ink-muted">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : (
        <>
          <p className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id="grow.games.group.progress"
              defaultMessage="{done} of {total} lists done"
              values={{ done, total }}
            />
          </p>
          <div className="mt-4 grid grid-cols-1 gap-5 @sm:grid-cols-2">
            {group.games.map((game) => (
              <CatalogueCard
                key={game.key}
                entry={{
                  // The id doubles as a DOM anchor (grow-game-<id>), where a dot would read as a class.
                  id: game.key.replace(/\./g, "-"),
                  title: intl.formatMessage({ id: game.titleId }),
                  description: intl.formatMessage({ id: game.descriptionId }),
                  href: `/grow/play/game/${game.key}`,
                  kind: "game",
                  status: gameTileStatus(sessions, game.key),
                  progress: "",
                }}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
